import { SENTIMENT_CONFIG } from './constants'

import type { SentimentState } from './SentimentIndicator.types'

const SENTIMENT_STATES = new Set<string>(Object.keys(SENTIMENT_CONFIG))

/**
 * Turns the raw `ai-sentiment` dropdown field value (a plain `string`) into a typed
 * `SentimentState`. Returns the value when it's one of the five known states
 * (`positive`, `neutral`, `negative`, `improving`, `declining`), or `null`
 * otherwise — e.g. when the field isn't set on the conversation. Callers can
 * then treat `null` as "no sentiment to show".
 */
export const toSentimentState = (
  value?: string | null
): SentimentState | null =>
  value != null && SENTIMENT_STATES.has(value)
    ? (value as SentimentState)
    : null

/**
 * Sentiment polarity as returned by the backend (e.g. the conversation
 * sentiment endpoint's `latestPolarity`). Defined locally so `packages/shared`
 * stays free of app-level dependencies.
 */
export type SentimentPolarity =
  | 'veryPositive'
  | 'positive'
  | 'neutral'
  | 'negative'
  | 'veryNegative'

/**
 * Collapses the five backend polarity values onto the three polarity-based
 * sentiment states. The trend states (`improving` / `declining`) have no
 * polarity equivalent and are never produced here. Returns `null` for an
 * unknown/absent polarity so callers can choose not to render an indicator.
 */
export const polarityToSentimentState = (
  polarity?: SentimentPolarity | null
): SentimentState | null => {
  switch (polarity) {
    case 'positive':
    case 'veryPositive':
      return 'positive'
    case 'neutral':
      return 'neutral'
    case 'negative':
    case 'veryNegative':
      return 'negative'
    default:
      return null
  }
}
